import { getOrderRequestDetail } from '../clients/order_request_client';
import { requestQuotesFromCatalog } from '../clients/quote_client';
import { logError, logInfo } from '../config_and_helpers/logger';
import {
  buildCatalogQuoteLinesFromOrderRequestLines,
  getQuotableQuoteJobsForSupplierGroup,
  getRequestedOrdersInSupplierGroup,
} from './orderRequestCart';

const LOG_CONTEXT = 'orderRequestQuoteBatch';

/**
 * Concatenate quote jobs into one `POST /quotes/request_from_catalog/` body.
 * Duplicate `product_id` rows are kept as separate lines. Quantities are not summed.
 * `order_request_id` is set only when `includeOrderRequestId` is true and every
 * contributing job shares the same positive id.
 *
 * @param {Array<{ orderRequestId?: number, lines?: Array<{ product_id: number, quantity: number }> }>} jobs
 * @param {{ includeOrderRequestId?: boolean }} [options]
 * @returns {{
 *   payload: { lines: Array<{ product_id: number, quantity: number }>, order_request_id?: number },
 *   orderRequestIds: number[],
 *   requestCount: number,
 * }}
 */
export function buildGroupedCatalogQuotePayload(
  jobs,
  { includeOrderRequestId = true } = {}
) {
  const lines = [];
  const orderRequestIds = [];
  let requestCount = 0;

  (Array.isArray(jobs) ? jobs : []).forEach(job => {
    const jobLines = Array.isArray(job?.lines) ? job.lines : [];
    if (jobLines.length === 0) return;
    requestCount += 1;
    jobLines.forEach(line => {
      lines.push({ ...line });
    });
    const id = Number(job?.orderRequestId);
    if (Number.isInteger(id) && id > 0) {
      orderRequestIds.push(id);
    }
  });

  const uniqueIds = [...new Set(orderRequestIds)];
  const everyJobSharesOneId =
    requestCount > 0 &&
    uniqueIds.length === 1 &&
    orderRequestIds.length === requestCount;
  const payload = { lines };
  if (includeOrderRequestId && everyJobSharesOneId) {
    payload.order_request_id = uniqueIds[0];
  }

  return {
    payload,
    orderRequestIds: uniqueIds,
    requestCount,
  };
}

/**
 * User-facing messages from a failed catalog quote-request response.
 *
 * @param {{ messages?: string[] }|null|undefined} response
 * @returns {string[]}
 */
function catalogQuoteFailureMessages(response) {
  if (Array.isArray(response?.messages) && response.messages.length > 0) {
    return response.messages.map(message => String(message));
  }
  return ['Request failed.'];
}

/**
 * Load line items on list rows when the inbox API omitted `lines`.
 *
 * @async
 * @param {string} token
 * @param {{ requests?: Array<Object> }} group
 * @returns {Promise<{ requests: Array<Object> }>}
 */
async function enrichSupplierGroupWithRequestLines(token, group) {
  const requests = await Promise.all(
    (Array.isArray(group?.requests) ? group.requests : []).map(async request => {
      if (request?.status !== 'REQUESTED') return request;
      if (Array.isArray(request.lines) && request.lines.length > 0) {
        return request;
      }
      const response = await getOrderRequestDetail(token, request.id);
      if (response?.success && response.data) {
        return response.data;
      }
      return request;
    })
  );
  return { ...group, requests };
}

/**
 * Send one catalog quote-request email batch for all REQUESTED rows in a supplier inbox group.
 * Lines from every quotable job are posted together. `order_request_id` is included only
 * when every included job belongs to the same order request.
 *
 * @async
 * @param {string} token
 * @param {{ supplierId?: *, supplierName?: string, requests?: Array<Object> }} group
 * @returns {Promise<{ success: boolean, data?: Object, messages: string[], requestCount: number }>}
 */
export async function requestQuotesForSupplierGroup(token, group) {
  if (getRequestedOrdersInSupplierGroup(group).length === 0) {
    return {
      success: false,
      messages: ['No open (REQUESTED) order requests in this supplier group.'],
      requestCount: 0,
    };
  }

  const enrichedGroup = await enrichSupplierGroupWithRequestLines(token, group);
  const jobs = getQuotableQuoteJobsForSupplierGroup(enrichedGroup);
  if (jobs.length === 0) {
    return {
      success: false,
      messages: ['No quotable lines in open requests for this supplier.'],
      requestCount: 0,
    };
  }

  const grouped = buildGroupedCatalogQuotePayload(jobs);
  logInfo(LOG_CONTEXT, 'requestQuotesForSupplierGroup submit', {
    supplierId: group?.supplierId ?? null,
    supplierName: group?.supplierName ?? '',
    lineCount: grouped.payload.lines.length,
    orderRequestIds: grouped.orderRequestIds,
    orderRequestIdSent: grouped.payload.order_request_id ?? null,
  });

  const response = await requestQuotesFromCatalog(token, grouped.payload);
  if (!response?.success) {
    const messages = catalogQuoteFailureMessages(response);
    logError(LOG_CONTEXT, 'requestQuotesForSupplierGroup failed', {
      supplierId: group?.supplierId ?? null,
      lineCount: grouped.payload.lines.length,
      orderRequestIds: grouped.orderRequestIds,
      orderRequestIdSent: grouped.payload.order_request_id ?? null,
      messages,
    });
    return {
      success: false,
      messages,
      requestCount: 0,
    };
  }

  logInfo(LOG_CONTEXT, 'requestQuotesForSupplierGroup success', {
    supplierId: group?.supplierId ?? null,
    lineCount: grouped.payload.lines.length,
    orderRequestIds: grouped.orderRequestIds,
    orderRequestIdSent: grouped.payload.order_request_id ?? null,
    corporateDemandRef: response.data?.corporate_demand_ref ?? '',
    supplierCount: response.data?.suppliers?.length ?? 0,
  });

  return {
    success: true,
    data: response.data,
    messages: [],
    requestCount: grouped.requestCount,
  };
}

/**
 * Load lines on a list row when the inbox API omitted `lines`.
 *
 * @async
 * @param {string} token
 * @param {Object} request
 * @returns {Promise<Object>}
 */
async function enrichOrderRequestWithLines(token, request) {
  if (request?.status !== 'REQUESTED') return request;
  if (Array.isArray(request.lines) && request.lines.length > 0) {
    return request;
  }
  const response = await getOrderRequestDetail(token, request.id);
  if (response?.success && response.data) {
    return response.data;
  }
  return request;
}

/**
 * Quote all suppliers for every open order request in the inbox list.
 * All quotable lines are sent in one catalog post. `order_request_id` is included
 * only when that list contains a single open order request.
 *
 * @async
 * @param {string} token
 * @param {Array<Object>} requests
 * @returns {Promise<{ success: boolean, data?: Object, messages: string[], requestCount: number }>}
 */
export async function requestQuotesForAllOrderRequests(token, requests) {
  const open = (Array.isArray(requests) ? requests : []).filter(
    row => row?.status === 'REQUESTED'
  );

  if (open.length === 0) {
    return {
      success: false,
      messages: ['No open (REQUESTED) order requests.'],
      requestCount: 0,
    };
  }

  const enriched = await Promise.all(
    open.map(request => enrichOrderRequestWithLines(token, request))
  );
  const jobs = enriched.map(request => ({
    orderRequestId: Number(request.id),
    requestRef: request.request_ref || `#${request.id}`,
    lines: buildCatalogQuoteLinesFromOrderRequestLines(request.lines),
  }));
  const quotableJobs = jobs.filter(job => job.lines.length > 0);

  if (quotableJobs.length === 0) {
    logInfo(LOG_CONTEXT, 'requestQuotesForAllOrderRequests skipped', {
      orderRequestIds: jobs.map(job => job.orderRequestId),
      reason: 'no quotable lines',
    });
    return {
      success: false,
      messages: jobs.map(job => `${job.requestRef}: No quotable lines.`),
      requestCount: 0,
    };
  }

  const grouped = buildGroupedCatalogQuotePayload(quotableJobs, {
    includeOrderRequestId: open.length === 1,
  });
  logInfo(LOG_CONTEXT, 'requestQuotesForAllOrderRequests submit', {
    lineCount: grouped.payload.lines.length,
    orderRequestIds: grouped.orderRequestIds,
    openRequestCount: open.length,
    orderRequestIdSent: grouped.payload.order_request_id ?? null,
  });

  const response = await requestQuotesFromCatalog(token, grouped.payload);
  if (!response?.success) {
    const messages = catalogQuoteFailureMessages(response);
    logError(LOG_CONTEXT, 'requestQuotesForAllOrderRequests failed', {
      lineCount: grouped.payload.lines.length,
      orderRequestIds: grouped.orderRequestIds,
      orderRequestIdSent: grouped.payload.order_request_id ?? null,
      messages,
    });
    return {
      success: false,
      messages,
      requestCount: 0,
    };
  }

  logInfo(LOG_CONTEXT, 'requestQuotesForAllOrderRequests success', {
    lineCount: grouped.payload.lines.length,
    orderRequestIds: grouped.orderRequestIds,
    orderRequestIdSent: grouped.payload.order_request_id ?? null,
    requestCount: grouped.requestCount,
    corporateDemandRef: response.data?.corporate_demand_ref ?? '',
    supplierCount: response.data?.suppliers?.length ?? 0,
  });

  return {
    success: true,
    data: response.data,
    messages: [],
    requestCount: grouped.requestCount,
  };
}
